import { NativeMessageExchange } from '@wetransfer/native-message-exchange-js';
import type {
  AuthWithRedirectParams,
  AdaptableAuthClient,
  AuthClientLoader,
} from '../adapter';
import type {
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  IdToken,
  LogoutOptions,
} from '@auth0/auth0-spa-js';
import { decodeJwt } from '../utils/jwt';
import { tokenRequestMessage, tokenResponseMessage } from './nativeMessages';

interface GetTokenResult {
  accessToken: string;
  idToken: string;
}

interface NativeAuthClientAdapterOptions {
  log?: Console['log'];
}

// 60 seconds
const TOKEN_EXPIRY_BUFFER = 60 * 1000;

export class NativeAuthClientAdapter implements AdaptableAuthClient {
  private _accessToken?: string;
  private _idToken?: string;
  private _expiry?: number;
  private _log: Console['log'];

  constructor({ log }: NativeAuthClientAdapterOptions) {
    this._log =
      // eslint-disable-next-line no-console
      log ?? process.env.NODE_ENV !== 'production' ? console.log : () => null;
    NativeMessageExchange.init();
  }

  private async _getTokens(): Promise<GetTokenResult> {
    const needNewTokens =
      !this._expiry || this._expiry - Date.now() < TOKEN_EXPIRY_BUFFER;

    if (needNewTokens) {
      this._log('_getTokens: needNewTokens');
      await this.checkSession();
    }

    const accessToken = this._accessToken as string;
    const idToken = this._idToken as string;

    if (accessToken && idToken) {
      return {
        accessToken,
        idToken,
      };
    } else {
      throw Error('nativeAuthClient: no tokens');
    }
  }

  async checkSession(_options?: GetTokenSilentlyOptions): Promise<void> {
    const request = tokenRequestMessage();

    try {
      const tokenResponse = await NativeMessageExchange.sendMessageToNative<
        ReturnType<typeof tokenResponseMessage>
      >(request);
      const { accessToken, idToken } = tokenResponse.payload;
      this._accessToken = accessToken;
      this._idToken = idToken;
      // Convert to milliseconds
      this._expiry = (decodeJwt(accessToken).claims.exp as number) * 1000;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    this._log('checkSession: complete');
  }

  async isAuthenticated(): Promise<boolean> {
    return !!this._accessToken;
  }

  async getIdTokenClaims(_options?: GetIdTokenClaimsOptions): Promise<IdToken> {
    return decodeJwt((await this._getTokens()).idToken).claims;
  }

  async getTokenSilently(_options?: GetTokenSilentlyOptions): Promise<string> {
    return (await this._getTokens()).accessToken;
  }

  async signUp(_params?: AuthWithRedirectParams): Promise<void> {
    this._log('nativeAuthClient: signUp');
  }

  async login(_params?: AuthWithRedirectParams): Promise<void> {
    this._log('nativeAuthClient: login');
  }

  async logoutSession(_options?: LogoutOptions): Promise<void> {
    this._log('nativeAuthClient: logoutSession');
  }

  async forgotPassword(_options?: AuthWithRedirectParams): Promise<void> {
    this._log('nativeAuthClient: forgotPassword');
  }

  clearLocalCache(): void {
    this._log('nativeAuthClient: clearLocalCache');
  }
}

let _nativeAuthClient: NativeAuthClientAdapter | undefined;

const nativeAuthClientAdapter = (
  options: NativeAuthClientAdapterOptions
): NativeAuthClientAdapter => {
  if (!_nativeAuthClient) {
    _nativeAuthClient = new NativeAuthClientAdapter(options);
  }

  return _nativeAuthClient;
};

export const nativeAuthClientAdapterLoader =
  (
    options: NativeAuthClientAdapterOptions
  ): AuthClientLoader<NativeAuthClientAdapter> =>
  () => {
    return nativeAuthClientAdapter(options);
  };
